import { useMemo } from "react";
import { buildBodyRootMap } from "../../utils/bodyHierarchy";
import { stripNamespacePrefix as stripPrefix } from "../../utils/nameUtils";

const getWorldBodyRootUuid = (worldBody) =>
  typeof worldBody === "string" ? worldBody : worldBody?.rootBodyUuid || worldBody?.uuid;

const getWorldBodyLabel = (worldBody, bodies, rootUuid) =>
  typeof worldBody === "object" && worldBody?.name
    ? worldBody.name
    : bodies?.[rootUuid]?.name || rootUuid;

export function usePrimaryRobotRootUuid(bodies, worldBodies) {
  return useMemo(() => {
    const roots = (worldBodies || [])
      .map((wb) => (typeof wb === "string" ? wb : wb?.uuid))
      .filter(Boolean);
    if (roots.length === 0) return null;
    if (roots.length === 1) return roots[0];

    const countJointsInSubtree = (rootUuid) => {
      let count = 0;
      const visited = new Set();
      const stack = [rootUuid];
      while (stack.length > 0) {
        const bodyUuid = stack.pop();
        if (!bodyUuid || visited.has(bodyUuid)) continue;
        visited.add(bodyUuid);
        const body = bodies?.[bodyUuid];
        if (!body) continue;
        count += Array.isArray(body.joints) ? body.joints.length : 0;
        (body.children || []).forEach((childUuid) => {
          if (childUuid && !visited.has(childUuid)) stack.push(childUuid);
        });
      }
      return count;
    };

    let bestRoot = roots[0];
    let bestScore = countJointsInSubtree(bestRoot);
    for (let i = 1; i < roots.length; i += 1) {
      const candidate = roots[i];
      const score = countJointsInSubtree(candidate);
      if (score > bestScore) {
        bestRoot = candidate;
        bestScore = score;
      }
    }
    return bestRoot;
  }, [worldBodies, bodies]);
}

export function useWorldRootTargetBodyUuid({ primaryRobotRootUuid, worldBodies, bodies }) {
  return useMemo(() => {
    if (primaryRobotRootUuid && bodies?.[primaryRobotRootUuid]) return primaryRobotRootUuid;
    const firstRootUuid = (worldBodies || [])
      .map((entry) => (typeof entry === "string" ? entry : entry?.uuid || entry?.rootBodyUuid))
      .find((uuid) => !!uuid && !!bodies?.[uuid]);
    return firstRootUuid || null;
  }, [primaryRobotRootUuid, worldBodies, bodies]);
}

export function useBodyDepthMap({ bodies, worldBodies, bodyParentMap }) {
  return useMemo(() => {
    const depthMap = new Map();
    const rootUuids = (worldBodies || []).map(getWorldBodyRootUuid).filter(Boolean);
    const stack = rootUuids.map((uuid) => ({ uuid, depth: 0 }));
    while (stack.length > 0) {
      const { uuid, depth } = stack.pop();
      if (!uuid || depthMap.has(uuid)) continue;
      depthMap.set(uuid, depth);
      const body = bodies?.[uuid];
      (body?.children || []).forEach((childUuid) =>
        stack.push({ uuid: childUuid, depth: depth + 1 })
      );
    }
    Object.keys(bodies || {}).forEach((uuid) => {
      if (depthMap.has(uuid)) return;
      let depth = 0;
      let cursor = uuid;
      const visited = new Set();
      while (cursor && !visited.has(cursor)) {
        visited.add(cursor);
        const parent = bodyParentMap[cursor] || null;
        if (!parent) break;
        depth += 1;
        cursor = parent;
      }
      depthMap.set(uuid, depth);
    });
    return depthMap;
  }, [bodies, worldBodies, bodyParentMap]);
}

export function useGroupedSceneGraphParentOptions({ bodies, worldBodies, isBodyAvailable }) {
  return useMemo(() => {
    const availableBodies = Object.values(bodies || {}).filter(isBodyAvailable);
    const rootMap = buildBodyRootMap(bodies || {}, worldBodies || []);
    const groupMap = new Map();

    (worldBodies || []).forEach((worldBody) => {
      const rootUuid = getWorldBodyRootUuid(worldBody);
      if (!rootUuid || groupMap.has(rootUuid)) return;
      groupMap.set(rootUuid, {
        label: getWorldBodyLabel(worldBody, bodies, rootUuid),
        bodies: [],
      });
    });

    availableBodies.forEach((body) => {
      const rootUuid = rootMap[body.uuid] || body.uuid;
      if (!groupMap.has(rootUuid)) {
        groupMap.set(rootUuid, { label: bodies?.[rootUuid]?.name || rootUuid, bodies: [] });
      }
      groupMap.get(rootUuid).bodies.push(body);
    });

    const groups = Array.from(groupMap.values())
      .map((group) => ({
        ...group,
        bodies: [...group.bodies].sort((a, b) =>
          String(stripPrefix(a.name) || "").localeCompare(String(stripPrefix(b.name) || ""))
        ),
      }))
      .filter((group) => group.bodies.length > 0);
    return groups.length > 1 ? groups : null;
  }, [bodies, worldBodies, isBodyAvailable]);
}
